"use client";

import { useEffect, useState } from "react";
import { Button } from "@/shadcn-components/ui/button";
import { cn } from "@/lib/utils";
import {
  formatUtcResetCountdown,
  isUnlimited,
  type AiCredits,
} from "@/lib/agent/ai-credits";

/** Where the Upgrade nudge points free users. */
const UPGRADE_URL = "https://hftbridge.com/pricing";

function useUtcResetCountdown(): string {
  const [label, setLabel] = useState(() => formatUtcResetCountdown());
  useEffect(() => {
    const id = setInterval(() => setLabel(formatUtcResetCountdown()), 30_000);
    return () => clearInterval(id);
  }, []);
  return label;
}

/** Gradient meter track matching the design-system `.meter` idiom (bid-blue → orange fill). */
function MeterBar({ pct }: { pct: number }) {
  return (
    <div className="h-2 overflow-hidden rounded-md border border-border bg-muted">
      <span
        className="block h-full rounded-md"
        style={{
          width: `${Math.max(0, Math.min(100, pct))}%`,
          background: "linear-gradient(90deg, #4B9BFF, #FF6B35)",
        }}
      />
    </div>
  );
}

export interface AgentCreditsMeterProps {
  credits: AiCredits | null;
  variant: "sidebar" | "mobile";
  className?: string;
}

/**
 * Daily AI-credits meter for the Vybe Agent sidebar. Shows "N / limit left" with a UTC reset countdown,
 * and flips to a friendly Upgrade wall when the free budget is exhausted. Reads `remaining` from the server;
 * the client counter is never authoritative.
 */
export function AgentCreditsMeter({
  credits,
  variant,
  className,
}: AgentCreditsMeterProps) {
  const countdown = useUtcResetCountdown();

  // Unlimited plans (Desk) or unknown state: render nothing — the meter is a free/Pro affordance.
  if (!credits || isUnlimited(credits)) return null;

  const { limit, remaining } = credits;
  const pct = limit > 0 ? (remaining / limit) * 100 : 0;
  const isMobile = variant === "mobile";
  const exhausted = remaining <= 0;

  if (exhausted) {
    return (
      <div
        className={cn(
          "rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5",
          className,
        )}
      >
        <div
          className={cn(
            "font-medium text-foreground",
            isMobile ? "text-sm" : "text-xs",
          )}
        >
          You&rsquo;re out of today&rsquo;s free AI credits
        </div>
        <div className="mb-2 mt-0.5 font-mono text-[10.5px] text-muted-foreground">
          0 / {limit} daily AI requests left
        </div>
        <MeterBar pct={0} />
        <div className="mt-2.5 flex items-center gap-3">
          <Button
            size="sm"
            className="h-6 px-3 text-[11px]"
            type="button"
            onClick={() => window.open(UPGRADE_URL, "_blank", "noopener")}
          >
            Upgrade
          </Button>
          <span className="font-mono text-[10px] text-muted-foreground">
            Resets 00:00 UTC · in {countdown}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-center justify-between font-mono text-[10.5px] text-muted-foreground">
        <span>AI credits</span>
        <span>
          <b className="font-semibold text-foreground">{remaining}</b> / {limit}{" "}
          left
        </span>
      </div>
      <MeterBar pct={pct} />
      <div className="flex items-center justify-between font-mono text-[10px] text-muted-foreground">
        <span>
          {remaining} / {limit} daily AI requests left
        </span>
        <span>
          resets in <b className="font-semibold text-foreground">{countdown}</b>
        </span>
      </div>
    </div>
  );
}
